import { CalloutContent, CalloutEmphasis, CalloutVariant } from '../../entities/callout-content.entity';
import { VerifyAccountUrl } from '../../constants/urls';
import { AlbertaSupportsContactCentreEmail, AlbertaSupportsContactCentreTollFree, HelpInfoDescription, PremadeContactBoxContent } from '../common/contact-info';

export const IesCallout_AccessCodePage_BasicUser = {
  calloutVariant: CalloutVariant.Important,
  headerText: 'Verify your account',
  bodyText: `Consider <a href="${VerifyAccountUrl}" target="_blank">verifying your account</a> 
  A verified account protects your identity and makes sure no one else can access services as you.<p>Verification will also allow you to submit all required signatures for your Income Support application immediately by providing digital consent - no printing needed.</p>`,
} as CalloutContent;

export const IesCallout_Dependents_ScheduleA = {
  calloutVariant: CalloutVariant.Information,
  headerText: 'Schedule A',
  outerTopContent: `<h3>Schedule A - Child Support Services</h3><br />`,
  bodyText: `The information you provide in this section will combine with information about you and your child/children to
        create a document called <strong>Schedule A</strong>. <br /><br />
        If you are eligible for Income Support, information from the <strong>Schedule A</strong> will be shared with
        Child Support Services (CSS) and a Child Support Worker may be in contact with you. CSS helps single parents or
        parents of blended households negotiate child support with parents who do not live in the household. You must
        participate with CSS to be eligible for Income Support benefits.`,
} as CalloutContent;

export const IesCallout_SignerPage_Verified = {
  calloutVariant: CalloutVariant.Information,
  headerText: 'Your Income Support Consent and Declaration is complete',
  bodyText: `You have successfully provided the Consent and Declaration for your Income Support application. 
  We've sent your spouse/partner an email to let them know that they can now submit the application.`,
} as CalloutContent;

export const IesCallout_SignerPage_Inactive = {
  calloutVariant: CalloutVariant.Information,
  headerText: 'Application not available',
  bodyText: `This application is no longer available for you to sign. Please speak to the person who shared it with you.`,
} as CalloutContent;

export const IesCallout_SignerPage_Rejected = {
  calloutVariant: CalloutVariant.Information,
  headerText: 'Consent and Declaration not provided',
  bodyText: `You have chosen not to provide Consent and Declaration at this time. Your spouse/partner can still submit the application by opting out of your Consent and Declaration.<p>A caseworker may follow up with you if needed.</p>`,
} as CalloutContent;

export const IesCallout_Dependents_Knockout_LivesWith = {
  calloutVariant: CalloutVariant.Emergency,
  headerText: 'Dependent must live with you',
  bodyText: `You cannot claim this child as a dependent because you indicated the child does not live with you.`,
} as CalloutContent;

export const IesCallout_Dependents_Knockout_OtherReasons = {
  calloutVariant: CalloutVariant.Emergency,
  headerText: 'This child does not qualify as a dependent',
  bodyText: `You cannot claim this child as a dependent because a <strong>Dependent child</strong> is a child who is dependent on
    you for support and lives with you and:

    <ul>
      <li>does not have a spouse or partner</li>
      <li>is under 18, or 18 or 19 and going to high school</li>
    </ul>`,
} as CalloutContent;

export const IesCallout_Dashboard_VerifyAccount = {
  calloutVariant: 'important',
  headerText: 'Verify your account',
  bodyText: `<b><a href="${VerifyAccountUrl}" target=_blank class='gaTrackEvent-verifyAccount'>Verify your Alberta.ca Account</a></b> for a better experience. 
  If you apply with a <b>Basic account</b>, a worker may require you to <b>Verify your Alberta.ca account</b> before they approve you for Income Support. 
  <p>Why verify?</p>
  <ul>
    <li><b>Apply faster</b> - You won’t need to download, sign, and upload some or all forms. </li>
    <li><b>Protect your identity</b> - Helps make sure no one else can use your account.</li>
  </ul>`,
} as CalloutContent;

export const IesCallout_Skip_Printing = {
  calloutVariant: CalloutVariant.Information,
  headerText: 'Want to skip printing and signing?',
  bodyText: `If you would like to sign this form digitally, verify your <a href="${VerifyAccountUrl}" target="_blank">Alberta.ca Account</a> . You will not need
  to print, sign, or upload the Consent and Declaration form. After verifying your account, sign out and sign back into your application - this page will change 
  and you will be able to provide your Consent and Declaration digitally by clicking the checkboxes.`,
} as CalloutContent;

export const IesCallout_Contact_Info = {
  calloutVariant: CalloutVariant.Information,
  headerText: 'Getting more help',
  bodyText: `${HelpInfoDescription}<br /><br />
    ${PremadeContactBoxContent}`,
} as CalloutContent;

export const IesCallout_Knockout = {
  calloutVariant: CalloutVariant.Information,
  headerText: 'Alberta Supports Contact Centre',
  bodyText: `Connect with the main Alberta Supports Contact Centre: <br />
    Hours: 7:30 am to 8 pm (open Monday to Friday, closed statutory holidays) <br />
    Toll free: <a href="tel:${AlbertaSupportsContactCentreTollFree}">${AlbertaSupportsContactCentreTollFree}</a> <br />Email: <a href="mailto:${AlbertaSupportsContactCentreEmail}">${AlbertaSupportsContactCentreEmail}</a>`,
} as CalloutContent;

export const IesCallout_ApplicationSettings_MaintenanceBanner = {
  calloutVariant: CalloutVariant.Information,
  headerText: '',
  bodyText: `Maintenance Banner will pull the site offline for non-administrators inside the selected date range.`,
} as CalloutContent;

export const IesCallout_ApplicationSettings_NotificationBanner = {
  calloutVariant: CalloutVariant.Information,
  headerText: '',
  bodyText: `Notification Banner will <u>not</u> display if Maintenance Banner is visible.`,
} as CalloutContent;

export const IesCallout_Dashboard_RemovedApplicationsRecovery = {
  calloutVariant: CalloutVariant.Important,
  headerText: '40-day recovery period',
  emphasis: CalloutEmphasis.Medium,
  bodyText: `You have 40 days starting from the date of removal to recover applications. After 40 days they will be permanently deleted.`,
} as CalloutContent;

export const IesCallout_Application_MissingDocuments = {
  calloutVariant: CalloutVariant.Important,
  headerText: 'Missing documents',
  bodyText: `The following is a list of documents that were received or missing from your application. Please collect the missing documents while you wait for the Alberta Supports worker to call you for your eligibility assessment.`,
} as CalloutContent;

export const IesCallout_MySituation_MedicalDentalEmergency = {
  calloutVariant: CalloutVariant.Information,
  headerText: 'Urgent Medical Prescription or Dental Emergency',
  bodyText: `If you have a medical prescription you need filled or a dental emergency you need attended in the next 48 hours, continue with this application and call the Alberta Supports Contact Centre for further direction and assistance toll free at <a href="tel:${AlbertaSupportsContactCentreTollFree}">${AlbertaSupportsContactCentreTollFree}</a> .`,
} as CalloutContent;

export const IesCallout_Declaration_Agreement = {
  calloutVariant: CalloutVariant.Information,
  headerText: 'Review application before you proceed',
  bodyText: `By agreeing to the Declaration, you are confirming this application is complete and accurate to the best of your knowledge. This digitally verified application is a legal document.`,
} as CalloutContent;
